import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Cookie } from 'lucide-react';
import api from '../utils/api';

const legalContent = {
  privacy: {
    title: 'Privacy Policy',
    icon: Shield,
    lastUpdated: 'December 2024',
    content: `
# Privacy Policy

## 1. Introduction

Nehpets+ Concepts ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our event management platform.

## 2. Information We Collect

### 2.1 Personal Information
- Name, email address, phone number
- Company and job title
- Billing and payment information
- Event preferences and requirements

### 2.2 Usage Information
- Log data and analytics
- Device and browser information
- IP address and location data

### 2.3 Event-Related Data
- Event details and specifications
- Vendor communications
- Project documentation

## 3. How We Use Your Information

We use the collected information to:
- Provide and improve our event management services
- Process transactions and send invoices
- Communicate about projects and services
- Send marketing communications (with consent)
- Comply with legal obligations
- Analyze usage patterns for service improvement

## 4. Data Sharing

We may share your information with:
- Service providers (payment processors, cloud services)
- Vendors assigned to your events
- Legal authorities when required by law
- Business partners with your explicit consent

## 5. Data Security

We implement industry-standard security measures including:
- SSL/TLS encryption for data in transit
- Encrypted storage for sensitive data
- Regular security audits and updates
- Access controls and authentication

## 6. Your Rights (GDPR)

You have the right to:
- Access your personal data
- Correct inaccurate data
- Request data deletion
- Object to data processing
- Data portability
- Withdraw consent

## 7. Data Retention

We retain your data for:
- Active accounts: Duration of service plus 7 years
- Inactive accounts: 2 years after last activity
- Legal compliance: As required by law

## 8. Contact Us

For privacy inquiries:
- Email: privacy@nehpets.com
- Phone: +234 803 326 0400
- Address: Victoria Island, Lagos, Nigeria
    `
  },
  terms: {
    title: 'Terms of Service',
    icon: FileText,
    lastUpdated: 'December 2024',
    content: `
# Terms of Service

## 1. Acceptance of Terms

By accessing and using Nehpets+ Concepts event management platform, you agree to be bound by these Terms of Service and our Privacy Policy.

## 2. Services

### 2.1 Description
Nehpets+ Concepts provides:
- Event consultancy and planning services
- Project management tools
- Vendor coordination
- AI-powered event tools
- Financial management features

### 2.2 Service Modifications
We reserve the right to modify, suspend, or discontinue services with reasonable notice.

## 3. User Accounts

### 3.1 Registration
- You must provide accurate, current information
- You are responsible for maintaining account security
- One account per user/organization

### 3.2 Account Termination
We may terminate accounts for:
- Violation of these terms
- Fraudulent activity
- Extended inactivity
- Non-payment of services

## 4. Payment Terms

### 4.1 Fees
- Service fees as quoted in proposals
- Subscription fees for platform access
- Additional charges for premium features

### 4.2 Payment Methods
- Bank transfer
- Card payments via Paystack
- Corporate invoicing

### 4.3 Refunds
Refund policies are outlined in individual service agreements.

## 5. Intellectual Property

### 5.1 Our Property
- Platform design and functionality
- AI tools and algorithms
- Branding and trademarks

### 5.2 Your Property
- Event content and materials you upload
- Client data and preferences
- Custom deliverables created for you

## 6. Limitations of Liability

Nehpets+ Concepts shall not be liable for:
- Indirect or consequential damages
- Loss of profits or data
- Third-party service failures
- Force majeure events

Maximum liability is limited to fees paid in the preceding 12 months.

## 7. Indemnification

You agree to indemnify Nehpets+ Concepts against claims arising from:
- Your use of the platform
- Violation of these terms
- Infringement of third-party rights

## 8. Governing Law

These terms are governed by the laws of the Federal Republic of Nigeria. Disputes shall be resolved in Lagos courts.

## 9. Contact

For legal inquiries:
- Email: legal@nehpets.com
- Address: Victoria Island, Lagos, Nigeria
    `
  },
  cookies: {
    title: 'Cookie Policy',
    icon: Cookie,
    lastUpdated: 'December 2024',
    content: `
# Cookie Policy

## 1. What Are Cookies?

Cookies are small text files stored on your device when you visit our platform. They help us provide a better user experience.

## 2. Types of Cookies We Use

### 2.1 Essential Cookies
- Authentication and session management
- Security features
- Platform functionality
- Cannot be disabled

### 2.2 Analytics Cookies
- Usage statistics
- Feature popularity
- Performance monitoring
- Can be disabled

### 2.3 Preference Cookies
- Language settings
- Theme preferences
- Display customizations
- Can be disabled

### 2.4 Marketing Cookies
- Ad personalization
- Campaign tracking
- Social media integration
- Require explicit consent

## 3. Third-Party Cookies

We use cookies from:
- Google Analytics (usage analytics)
- Paystack (payment processing)
- Intercom (customer support)

## 4. Managing Cookies

### 4.1 Browser Settings
Most browsers allow you to:
- Block all cookies
- Block third-party cookies
- Clear existing cookies
- Enable "Do Not Track"

### 4.2 Platform Settings
You can manage cookie preferences in your account settings.

## 5. Impact of Disabling Cookies

Disabling certain cookies may:
- Affect platform functionality
- Require repeated logins
- Disable personalization features
- Limit analytics capabilities

## 6. Cookie Retention

- Session cookies: Deleted when browser closes
- Persistent cookies: Up to 2 years
- Analytics cookies: 26 months

## 7. Updates to This Policy

We may update this policy periodically. Changes will be posted here with a new "Last Updated" date.

## 8. Contact

For cookie-related inquiries:
- Email: privacy@nehpets.com
    `
  }
};

const LegalPage = () => {
  const { type } = useParams();
  const content = legalContent[type] || legalContent.privacy;
  const Icon = content.icon;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#0A0A0F] border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/" className="inline-flex items-center text-white/60 hover:text-[#D4AF37] transition-colors mb-4">
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center">
            <Icon className="text-[#D4AF37] mr-4" size={32} />
            <div>
              <h1 className="text-3xl font-medium">{content.title}</h1>
              <p className="text-white/60 text-sm">Last updated: {content.lastUpdated}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-8">
          <div className="prose prose-invert max-w-none prose-headings:text-[#D4AF37] prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-white/70 prose-li:text-white/70 prose-strong:text-white">
            {content.content.split('\n').map((line, i) => {
              if (line.startsWith('# ')) {
                return <h1 key={i} className="text-2xl font-bold text-[#D4AF37] mt-8 mb-4">{line.replace('# ', '')}</h1>;
              } else if (line.startsWith('## ')) {
                return <h2 key={i} className="text-xl font-semibold text-white mt-6 mb-3">{line.replace('## ', '')}</h2>;
              } else if (line.startsWith('### ')) {
                return <h3 key={i} className="text-lg font-medium text-white/90 mt-4 mb-2">{line.replace('### ', '')}</h3>;
              } else if (line.startsWith('- ')) {
                return <li key={i} className="text-white/70 ml-4">{line.replace('- ', '')}</li>;
              } else if (line.trim()) {
                return <p key={i} className="text-white/70 mb-3">{line}</p>;
              }
              return null;
            })}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 flex flex-wrap gap-4">
          {Object.entries(legalContent).map(([key, value]) => (
            <Link
              key={key}
              to={`/legal/${key}`}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                type === key
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              {value.title}
            </Link>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-white/40 text-sm">
          <p>© {new Date().getFullYear()} Nehpets+ Concepts. All rights reserved.</p>
          <p className="mt-2">
            Questions? Contact us at{' '}
            <a href="mailto:legal@nehpets.com" className="text-[#D4AF37] hover:underline">
              legal@nehpets.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
